import React from 'react';
import { api } from '../lib/api';
import { money } from '../lib/format';
import { Failed, Loading, Stat, type Ctx } from '../main';
import { useResource } from '../lib/hooks';

/**
 * Six months of contracted income.
 *
 * Deliberately conservative: only leases that exist, only costs the engine has
 * already created. The gap between this and a full portfolio is the vacancy
 * upside, and it is more useful shown as a gap than smoothed over with guesses.
 */
export function Projections({ ctx: _ctx }: { ctx: Ctx }): React.JSX.Element {
  const leases = useResource(() => api.leases(), []);
  const properties = useResource(() => api.properties(), []);

  if (leases.loading || properties.loading) return <Loading />;
  if (leases.error) return <Failed error={leases.error} onRetry={leases.reload} />;

  const active = (leases.data?.leases ?? []).filter((l) => l.status === 'Active');
  const props = properties.data?.properties ?? [];
  const rooms = props.reduce((a, b) => a + b.roomCount, 0);
  const occupied = props.reduce((a, b) => a + b.occupiedCount, 0);
  const avgRent = active.length
    ? active.reduce((a, b) => a + Number(b.monthlyRent), 0) / active.length : 0;

  const months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() + i + 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    // a lease only contributes while it is still running
    const contracted = active
      .filter((l) => l.endDate >= end)
      .reduce((a, b) => a + Number(b.monthlyRent), 0);
    return {
      label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      contracted,
      expiring: active.filter((l) => l.endDate < end && l.endDate >= new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)).length,
    };
  });
  const peak = Math.max(...months.map((m) => m.contracted), 1);
  const total = months.reduce((a, b) => a + b.contracted, 0);

  return (
    <>
      <div className="grid g3" style={{ marginBottom: 16 }}>
        <Stat label="Contracted, six months" value={money(total)} tone="good" />
        <Stat label="Active leases" value={active.length} foot={`Average ${money(avgRent)}/mo`} />
        <Stat label="Vacancy upside" value={money((rooms - occupied) * avgRent)}
          foot={`${rooms - occupied} rooms not yet let, per month`} />
      </div>

      <div className="card"><div className="bd">
        {months.map((m) => (
          <div key={m.label} style={{ marginBottom: 14 }}>
            <div className="between" style={{ fontSize: 12.5, marginBottom: 5 }}>
              <b>{m.label}</b>
              <span>
                {m.expiring > 0 && (
                  <span className="tag t-warn" style={{ marginRight: 8 }}>
                    {m.expiring} lease{m.expiring === 1 ? '' : 's'} expiring
                  </span>
                )}
                <span className="mono">{money(m.contracted)}</span>
              </span>
            </div>
            <div className="wfbar">
              <i style={{ width: `${(m.contracted / peak) * 100}%`, background: 'var(--teal)' }} />
            </div>
          </div>
        ))}
      </div></div>

      <div className="note" style={{ marginTop: 16 }}>
        Signed leases only: empty rooms and unsigned renewals are not counted, so later months look lower.
      </div>
    </>
  );
}
